import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useApp } from "../../context/AppContext";
import { Kicker } from "../motion";
import regionMapTablet from "../../assets/sections/region-map-tablet.webp";

function CountUp({ target, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
}

export function KeyFigures() {
  const { c } = useApp();
  const f = c.figures;

  return (
    <section data-testid="figures-section" className="grain relative overflow-hidden bg-[#002355] py-24 md:py-32">
      <img
        src={regionMapTablet}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-70"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,35,85,0.98)_0%,rgba(0,35,85,0.9)_36%,rgba(0,35,85,0.48)_68%,rgba(0,35,85,0.22)_100%)]"
      />
      <div
        className="pointer-events-none absolute -right-32 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle,#2f7d63 0%,transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-[1240px] px-5 md:px-8">
        <Kicker tone="cream">{f.kicker}</Kicker>
        <h2 className="mt-5 max-w-xl font-display text-4xl font-bold tracking-tight text-[#f5f7fa] sm:text-5xl">
          {f.title}
        </h2>

        <div className="mt-16 grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-0">
          {f.stats.map((s, i) => (
            <div key={i} data-testid={`figure-${i}`} className="relative lg:px-8 lg:first:pl-0">
              {i > 0 && <span className="absolute left-0 top-1 hidden h-24 w-px bg-[#e8a93d]/25 lg:block" />}
              <div className="font-display text-6xl font-extrabold tracking-tighter text-[#e8a93d] lg:text-7xl">
                <CountUp target={s.num} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <p className="mt-3 max-w-[200px] text-sm text-[#f5f7fa]/70">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
